import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import clsx from "clsx";
import { useOcSessionStore } from "@/store/oc-session-store";
import { ocHasPermission, type OcPermissionKey } from "@oc/types";

// ─── Nav item definition ──────────────────────────────────────────────────────

export interface OcNavItem {
  icon: React.ReactNode;
  label: string;
  href: string;
  permission?: OcPermissionKey;
  group: string;
  external?: boolean;
}

// ─── SVG icon primitives ──────────────────────────────────────────────────────

function IconGrid() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path d="M5 3a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2H5ZM5 11a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2v-2a2 2 0 0 0-2-2H5ZM11 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2V5ZM11 13a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2v-2Z" />
    </svg>
  );
}
function IconUsers() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path d="M7 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM14.5 9a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM1.615 16.428a1.224 1.224 0 0 1-.569-1.175 6.002 6.002 0 0 1 11.908 0c.058.467-.172.92-.57 1.174A9.953 9.953 0 0 1 7 18a9.953 9.953 0 0 1-5.385-1.572ZM14.5 16h-.106c.07-.297.088-.611.048-.933a7.47 7.47 0 0 0-1.588-3.755 4.502 4.502 0 0 1 5.874 2.636.818.818 0 0 1-.36.98A7.465 7.465 0 0 1 14.5 16Z" />
    </svg>
  );
}
function IconAudit() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path fillRule="evenodd" d="M4.5 2A1.5 1.5 0 0 0 3 3.5v13A1.5 1.5 0 0 0 4.5 18h11a1.5 1.5 0 0 0 1.5-1.5V7.621a1.5 1.5 0 0 0-.44-1.06l-4.12-4.122A1.5 1.5 0 0 0 11.378 2H4.5Zm2.25 8.5a.75.75 0 0 0 0 1.5h6.5a.75.75 0 0 0 0-1.5h-6.5Zm0 3a.75.75 0 0 0 0 1.5h6.5a.75.75 0 0 0 0-1.5h-6.5Z" clipRule="evenodd" />
    </svg>
  );
}
function IconReports() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path d="M15.5 2A1.5 1.5 0 0 0 14 3.5v13a1.5 1.5 0 0 0 3 0v-13A1.5 1.5 0 0 0 15.5 2ZM9.5 6A1.5 1.5 0 0 0 8 7.5v9a1.5 1.5 0 0 0 3 0v-9A1.5 1.5 0 0 0 9.5 6ZM3.5 10A1.5 1.5 0 0 0 2 11.5v5a1.5 1.5 0 0 0 3 0v-5A1.5 1.5 0 0 0 3.5 10Z" />
    </svg>
  );
}
function IconSettings() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path fillRule="evenodd" d="M7.84 1.804A1 1 0 0 1 8.82 1h2.36a1 1 0 0 1 .98.804l.331 1.652a6.993 6.993 0 0 1 1.929 1.115l1.598-.54a1 1 0 0 1 1.186.447l1.18 2.044a1 1 0 0 1-.205 1.251l-1.267 1.113a7.047 7.047 0 0 1 0 2.228l1.267 1.113a1 1 0 0 1 .206 1.25l-1.18 2.045a1 1 0 0 1-1.187.447l-1.598-.54a6.993 6.993 0 0 1-1.929 1.115l-.33 1.652a1 1 0 0 1-.98.804H8.82a1 1 0 0 1-.98-.804l-.331-1.652a6.993 6.993 0 0 1-1.929-1.115l-1.598.54a1 1 0 0 1-1.186-.447l-1.18-2.044a1 1 0 0 1 .205-1.251l1.267-1.114a7.05 7.05 0 0 1 0-2.227L1.821 7.773a1 1 0 0 1-.206-1.25l1.18-2.045a1 1 0 0 1 1.187-.447l1.598.54A6.992 6.992 0 0 1 7.51 3.456l.33-1.652ZM10 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" clipRule="evenodd" />
    </svg>
  );
}
function IconWorkflows() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path d="M11.983 1.907a.75.75 0 0 0-1.292-.657l-8.5 9.5A.75.75 0 0 0 2.75 12h6.572l-1.305 6.093a.75.75 0 0 0 1.292.657l8.5-9.5A.75.75 0 0 0 17.25 8h-6.572l1.305-6.093Z" />
    </svg>
  );
}
function IconConnections() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path fillRule="evenodd" d="M12.207 2.793a1 1 0 0 1 1.414 0l3.586 3.586a1 1 0 0 1 0 1.414l-1.293 1.293a1 1 0 0 1-1.414 0L13.086 7.67l-1.793 1.793.416.416a1 1 0 0 1 0 1.414l-.793.793a1 1 0 0 1-1.414 0l-.416-.416-3.5 3.5.416.416a1 1 0 0 1 0 1.414l-.793.793a1 1 0 0 1-1.414 0L2.21 16.207a1 1 0 0 1 0-1.414l.793-.793a1 1 0 0 1 1.414 0l.416.416 3.5-3.5-.416-.416a1 1 0 0 1 0-1.414l.793-.793a1 1 0 0 1 1.414 0l.416.416L12.33 6.916l-.416-.416a1 1 0 0 1 0-1.414l.293-.293Z" clipRule="evenodd" />
    </svg>
  );
}
function IconDeveloper() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path fillRule="evenodd" d="M6.28 5.22a.75.75 0 0 1 0 1.06L2.56 10l3.72 3.72a.75.75 0 0 1-1.06 1.06L1 10.53a.75.75 0 0 1 0-1.06l4.22-4.25a.75.75 0 0 1 1.06 0Zm7.44 0a.75.75 0 0 1 1.06 0L19 9.47a.75.75 0 0 1 0 1.06l-4.22 4.25a.75.75 0 0 1-1.06-1.06L17.44 10l-3.72-3.72a.75.75 0 0 1 0-1.06ZM11.377 2.011a.75.75 0 0 1 .612.867l-2.5 14.5a.75.75 0 0 1-1.478-.255l2.5-14.5a.75.75 0 0 1 .866-.612Z" clipRule="evenodd" />
    </svg>
  );
}
function IconSSO() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path fillRule="evenodd" d="M8 7a5 5 0 1 1 3.61 4.804l-1.903 1.903A1 1 0 0 1 9 14H8v1a1 1 0 0 1-1 1H6v1a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-2a1 1 0 0 1 .293-.707L7.196 10.39A5.002 5.002 0 0 1 8 7Zm5-1a1 1 0 1 0 0 2 1 1 0 0 0 0-2Z" clipRule="evenodd" />
    </svg>
  );
}
function IconBook() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path d="M10.75 16.82A7.462 7.462 0 0 1 15 15.5c.71 0 1.396.098 2.046.282A.75.75 0 0 0 18 15.06V4.25a.75.75 0 0 0-.546-.721A9.006 9.006 0 0 0 15 3.25c-1.85 0-3.55.543-4.97 1.465L10 4.74v12.02l.75.06ZM9.25 4.74 9.22 4.715A7.462 7.462 0 0 0 5 3.25c-.88 0-1.73.15-2.516.431A.75.75 0 0 0 2 4.397v10.723a.75.75 0 0 0 .984.714A7.463 7.463 0 0 1 5 15.5c1.598 0 3.073.5 4.27 1.345L9.25 16.76V4.74Z" />
    </svg>
  );
}
function IconSupport() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-2 0c0 .993-.241 1.929-.668 2.754l-1.524-1.525a3.997 3.997 0 0 0 .078-2.183l1.562-1.562C15.802 8.249 16 9.1 16 10Zm-5.165 3.913 1.58 1.58A5.98 5.98 0 0 1 10 16a5.976 5.976 0 0 1-2.516-.552l1.562-1.562a4.006 4.006 0 0 0 1.789.027Zm-4.677-2.796a4.002 4.002 0 0 1-.041-2.08l-.08.08-1.53-1.533A5.98 5.98 0 0 0 4 10c0 .954.223 1.856.619 2.657l1.54-1.54Zm1.088-6.45A5.974 5.974 0 0 1 10 4c.954 0 1.856.223 2.657.619l-1.54 1.54a4.002 4.002 0 0 0-2.346.033L7.246 4.668ZM12 10a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z" clipRule="evenodd" />
    </svg>
  );
}
function IconEmail() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path d="M3 4a2 2 0 0 0-2 2v1.161l8.441 4.221a1.25 1.25 0 0 0 1.118 0L19 7.162V6a2 2 0 0 0-2-2H3Z" />
      <path d="m19 8.839-7.77 3.885a2.75 2.75 0 0 1-2.46 0L1 8.839V14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8.839Z" />
    </svg>
  );
}
function IconBell() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path fillRule="evenodd" d="M4 8a6 6 0 1 1 12 0c0 3.098 1.159 5.42 2.134 6.897A.75.75 0 0 1 17.5 16h-15a.75.75 0 0 1-.634-1.103C2.841 13.42 4 11.098 4 8Zm6 9.5A2.5 2.5 0 0 1 7.5 15h5A2.5 2.5 0 0 1 10 17.5Z" clipRule="evenodd" />
    </svg>
  );
}
function IconPlanUsage() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM8.798 7.045a3.372 3.372 0 0 0-.488.437A1.875 1.875 0 0 0 7.875 8.75c0 .414.168.79.435 1.063.244.25.573.427.94.499V11.5a.75.75 0 0 0 1.5 0v-.25h.25a1.875 1.875 0 0 0 0-3.75h-.625v-.875a.75.75 0 0 0-1.5 0v.875h-.077Zm1.577 1.375v.875h.25a.375.375 0 1 0 0-.75h-.25v-.125ZM8.25 9.813a.375.375 0 0 1 .375-.375h.125v.75h-.125a.375.375 0 0 1-.375-.375ZM10.375 5a.75.75 0 0 0 0 1.5.375.375 0 1 1 0 .75.75.75 0 0 0 0 1.5 1.875 1.875 0 0 0 0-3.75Z" clipRule="evenodd" />
    </svg>
  );
}
function IconSearch() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z" />
    </svg>
  );
}
function IconProvisioning() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path d="M6 10a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM8.25 9.75a.75.75 0 0 0 0 1.5h9.5a.75.75 0 0 0 0-1.5h-9.5ZM12 5a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM14.25 4.25a.75.75 0 0 0 0 1.5h3.5a.75.75 0 0 0 0-1.5h-3.5ZM2 4.25a.75.75 0 0 1 .75-.75h5.5a.75.75 0 0 1 0 1.5h-5.5A.75.75 0 0 1 2 4.25ZM16 15a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM2 14.25a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5h-7.5A.75.75 0 0 1 2 14.25Z" />
    </svg>
  );
}
function IconSliders() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path d="M10 3.75a2 2 0 1 0-1.732 1A2.99 2.99 0 0 0 8 6.5v7a.75.75 0 0 0 1.5 0v-7a3 3 0 0 0-.268-.75A2 2 0 0 0 10 3.75ZM5.732 12A2 2 0 1 0 4 13v.5a.75.75 0 0 0 1.5 0V13a2 2 0 0 0 .232-1ZM4 4.75a.75.75 0 0 1 1.5 0v3.5a.75.75 0 0 1-1.5 0v-3.5ZM15.5 6.5a.75.75 0 0 0-1.5 0v.5a2 2 0 0 0 .232 1A2 2 0 1 0 16 7a2.99 2.99 0 0 0-.5-.5ZM14 11.75a.75.75 0 0 1 1.5 0v3.5a.75.75 0 0 1-1.5 0v-3.5Z" />
    </svg>
  );
}
function IconSelfService() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path fillRule="evenodd" d="M10 1a4.5 4.5 0 0 0-4.5 4.5V9H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2h-.5V5.5A4.5 4.5 0 0 0 10 1Zm3 8V5.5a3 3 0 1 0-6 0V9h6Z" clipRule="evenodd" />
    </svg>
  );
}
function IconRoles() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path fillRule="evenodd" d="M9.661 2.237a.531.531 0 0 1 .678 0 11.947 11.947 0 0 0 7.078 2.749.5.5 0 0 1 .479.425c.069.52.104 1.05.104 1.589 0 5.162-3.26 9.563-7.834 11.256a.48.48 0 0 1-.332 0C5.26 16.563 2 12.162 2 7c0-.538.035-1.069.104-1.589a.5.5 0 0 1 .48-.425 11.947 11.947 0 0 0 7.077-2.749Zm4.196 5.954a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z" clipRule="evenodd" />
    </svg>
  );
}

// ─── Nav items with groups ────────────────────────────────────────────────────

export const OC_NAV_ITEMS: OcNavItem[] = [
  // Overview
  { icon: <IconGrid />,         label: "Dashboard",        href: "/dashboard",              permission: "dashboard.view",               group: "Overview" },
  { icon: <IconUsers />,        label: "Directory Search",   href: "/directory-search",       permission: "students.view",                group: "Overview" },
  // Integrations
  { icon: <IconConnections />,  label: "Connections",       href: "/connections",            permission: "connections.view",             group: "Integrations" },
  { icon: <IconWorkflows />,    label: "Workflows",         href: "/workflows",              permission: "provisioning_rules.view",      group: "Integrations" },
  { icon: <IconSearch />,       label: "Record Management",    href: "/record-management",      permission: "search.view",                  group: "Integrations" },
  { icon: <IconSliders />,      label: "Actions Configuration", href: "/actions-configuration", permission: "actions.view",                 group: "Integrations" },
  { icon: <IconSelfService />,  label: "Self-Service",      href: "/self-service",           permission: "selfservice.view",             group: "Settings" },
  // Provisioning
  { icon: <IconProvisioning />, label: "Provision Rules",   href: "/provision-rules",        permission: "provisioning_settings.view",   group: "Provisioning" },
  // Reporting
  { icon: <IconAudit />,        label: "Audit Log",         href: "/audit-log",              permission: "audit_log.view",               group: "Reporting" },
  { icon: <IconAudit />,        label: "Password Reset Log", href: "/password-reset-log",    permission: "selfservice.log.view",         group: "Reporting" },
  { icon: <IconReports />,      label: "Reports",           href: "/reports",                permission: "reports.view",                 group: "Reporting" },
  { icon: <IconPlanUsage />,    label: "Plan & Usage",      href: "/plan-usage",             permission: "billing.view",                 group: "Reporting" },
  // Settings
  { icon: <IconRoles />,        label: "Users & Roles",     href: "/users-roles",            permission: "users.view",                   group: "Settings" },
  { icon: <IconEmail />,        label: "Email Templates",   href: "/email-templates",        permission: "email_templates.configure",    group: "Settings" },
  { icon: <IconSSO />,          label: "SSO Settings",      href: "/sso-settings",           permission: "sso.view",                     group: "Settings" },
  { icon: <IconBell />,         label: "Alerts",            href: "/alerts",                 permission: "alerts.view",                  group: "Reporting" },
  { icon: <IconDeveloper />,    label: "Developer",         href: "/developer",              permission: "developer.view",               group: "Settings" },
  // Help
  { icon: <IconBook />,         label: "User Manual",       href: "https://oc-user-manual.pages.dev/", external: true,  group: "Help" },
  { icon: <IconSupport />,      label: "Support",           href: "/support",                permission: "support.view",                 group: "Help" },
];

const OC_GROUP_ORDER = ["Overview", "Integrations", "Provisioning", "Reporting", "Settings", "Help"];

// ─── Component ────────────────────────────────────────────────────────────────

export interface OcSidebarNavProps {
  collapsed: boolean;
}

export function OcSidebarNav({ collapsed }: OcSidebarNavProps) {
  const permissions = useOcSessionStore((s) => s.permissions);
  const location = useLocation();

  const visibleItems = OC_NAV_ITEMS.filter((item) =>
    !item.permission || ocHasPermission(permissions, item.permission)
  );

  const groups = OC_GROUP_ORDER
    .map((label) => ({ label, items: visibleItems.filter((i) => i.group === label) }))
    .filter((g) => g.items.length > 0);

  return (
    <nav
      className="flex flex-col gap-0 px-2 py-3 flex-1 overflow-y-auto"
      aria-label="Main navigation"
    >
      {groups.map((group, gIdx) => (
        <div key={group.label} className={clsx("flex flex-col", gIdx > 0 && "mt-4")}>
          {/* Group label — hidden when collapsed; divider shown instead */}
          {collapsed ? (
            gIdx > 0 && (
              <div className="my-1 mx-1 h-px bg-slate-200 dark:bg-oc-navy-700" />
            )
          ) : (
            <p className="px-2.5 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 select-none">
              {group.label}
            </p>
          )}

          <div className="flex flex-col gap-0.5">
            {group.items.map((item) => {
              const isActive =
                item.href === "/dashboard"
                  ? location.pathname === "/dashboard"
                  : location.pathname.startsWith(item.href);

              const linkCls = clsx(
                "flex items-center gap-3 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-oc-teal-500",
                isActive
                  ? "bg-oc-teal-500/10 text-oc-teal-600 dark:text-oc-teal-400"
                  : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-oc-navy-700 hover:text-slate-900 dark:hover:text-slate-200"
              );

              if (item.external) {
                return (
                  <a
                    key={item.href}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={collapsed ? item.label : undefined}
                    className={linkCls}
                  >
                    <span className="flex-shrink-0">{item.icon}</span>
                    {!collapsed && (
                      <span className="truncate leading-snug">{item.label}</span>
                    )}
                  </a>
                );
              }

              return (
                <NavLink
                  key={item.href}
                  to={item.href}
                  title={collapsed ? item.label : undefined}
                  className={linkCls}
                >
                  <span className="flex-shrink-0">{item.icon}</span>
                  {!collapsed && (
                    <span className="truncate leading-snug">{item.label}</span>
                  )}
                </NavLink>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
}
